import React, { useState, useEffect, useRef, useCallback } from 'react';
import { HelpCircle, X, Send, Loader2, History, MessageSquare, Move } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import { useFAQChat } from '@/hooks/useFAQChat';
import { formatDateTime } from '@/lib/date';

/**
 * Validate URL to prevent XSS (only allow relative URLs or https)
 */
function isSafeUrl(url: string): boolean {
  if (url.startsWith('/')) return true;
  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

interface FAQChatButtonProps {
  className?: string;
}

export function FAQChatButton({ className }: FAQChatButtonProps) {
  // Feature flag: disable FAQ chat in production until DB is ready
  if (import.meta.env.VITE_ENABLE_FAQ_CHAT === 'false') {
    return null;
  }

  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState('');
  const [activeTab, setActiveTab] = useState<'chat' | 'history'>('chat');
  const {
    messages,
    isLoading,
    sendMessage,
    clearMessages,
    sessionHistory,
    isLoadingHistory,
    fetchSessionHistory,
    loadSession,
  } = useFAQChat();

  // Auto-scroll ref
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Draggable button state (mobile only)
  const [buttonPosition, setButtonPosition] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('faq-button-position');
      if (saved) {
        try {
          return JSON.parse(saved);
        } catch {
          // ignore
        }
      }
    }
    return { x: 0, y: 0 }; // 0,0 means default position (CSS handles it)
  });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const longPressTimer = useRef<NodeJS.Timeout | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    const query = input.trim();
    setInput('');
    await sendMessage(query);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleOpen = () => {
    setIsOpen(true);
  };

  // CB-06U: Fetch history when switching to history tab
  useEffect(() => {
    if (activeTab === 'history') {
      fetchSessionHistory();
    }
  }, [activeTab, fetchSessionHistory]);

  const handleLoadSession = (session: typeof sessionHistory[0]) => {
    loadSession({ id: session.id, messages: session.messages });
    setActiveTab('chat');
  };

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isLoading]);

  // Draggable button handlers (mobile long-press to drag)
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    // Prevent browser context menu on long-press
    e.preventDefault();

    const touch = e.touches[0];
    setDragStart({ x: touch.clientX, y: touch.clientY });

    // Start long-press timer (500ms to enter drag mode)
    longPressTimer.current = setTimeout(() => {
      setIsDragging(true);
      // Vibrate for haptic feedback if available
      if (navigator.vibrate) {
        navigator.vibrate(50);
      }
    }, 500);
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    // Cancel long-press if moved before timer completes
    if (!isDragging && longPressTimer.current) {
      const touch = e.touches[0];
      const dx = Math.abs(touch.clientX - dragStart.x);
      const dy = Math.abs(touch.clientY - dragStart.y);
      if (dx > 10 || dy > 10) {
        clearTimeout(longPressTimer.current);
        longPressTimer.current = null;
      }
    }

    // Handle dragging
    if (isDragging) {
      e.preventDefault();
      const touch = e.touches[0];
      const newX = window.innerWidth - touch.clientX - 28; // 28 = half button width
      const newY = window.innerHeight - touch.clientY - 28;

      // Keep within screen bounds
      const clampedX = Math.max(8, Math.min(window.innerWidth - 64, newX));
      const clampedY = Math.max(80, Math.min(window.innerHeight - 64, newY));

      setButtonPosition({ x: clampedX, y: clampedY });
    }
  }, [isDragging, dragStart]);

  const handleTouchEnd = useCallback(() => {
    // Clear long-press timer
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }

    // Save position if was dragging
    if (isDragging) {
      localStorage.setItem('faq-button-position', JSON.stringify(buttonPosition));
      setIsDragging(false);
    }
  }, [isDragging, buttonPosition]);

  // Reset position handler
  const handleResetPosition = () => {
    setButtonPosition({ x: 0, y: 0 });
    localStorage.removeItem('faq-button-position');
  };

  return (
    <>
      {/* Floating Button - Draggable on mobile with long-press */}
      {!isOpen && (
        <Button
          ref={buttonRef}
          onClick={isDragging ? undefined : handleOpen}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          className={cn(
            'fixed z-50 h-14 w-14 rounded-full shadow-lg',
            'bg-primary hover:bg-primary/90',
            'transition-transform hover:scale-105',
            'touch-none select-none', // Prevent browser long-press menu
            isDragging && 'scale-110 shadow-2xl ring-2 ring-primary/50',
            className
          )}
          style={
            buttonPosition.x !== 0 || buttonPosition.y !== 0
              ? {
                  right: `${buttonPosition.x}px`,
                  bottom: `${buttonPosition.y}px`,
                }
              : {
                  right: '24px',
                  bottom: '24px',
                }
          }
          size="icon"
          aria-label={isDragging ? 'ドラッグ中' : 'ヘルプを開く'}
        >
          {isDragging ? (
            <Move className="h-6 w-6" />
          ) : (
            <HelpCircle className="h-6 w-6" />
          )}
        </Button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <Card
          className={cn(
            'fixed bottom-20 right-4 md:bottom-6 md:right-6 z-50',
            'w-[calc(100vw-2rem)] max-w-[380px] h-[60vh] md:h-[500px] max-h-[80vh]',
            'flex flex-col shadow-xl',
            'animate-in slide-in-from-bottom-4 fade-in duration-200'
          )}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b bg-primary text-primary-foreground rounded-t-lg">
            <div className="flex items-center gap-2">
              <HelpCircle className="h-5 w-5" />
              <span className="font-medium">お問い合わせ</span>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleClose}
              className="h-8 w-8 text-primary-foreground hover:bg-primary-foreground/20"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* CB-06U: Tabs for Chat / History */}
          <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'chat' | 'history')} className="flex-1 flex flex-col min-h-0">
            <TabsList className="grid w-full grid-cols-2 mx-4 mt-2" style={{ width: 'calc(100% - 2rem)' }}>
              <TabsTrigger value="chat" className="gap-1">
                <MessageSquare className="h-3.5 w-3.5" />
                チャット
              </TabsTrigger>
              <TabsTrigger value="history" className="gap-1">
                <History className="h-3.5 w-3.5" />
                履歴
              </TabsTrigger>
            </TabsList>

            {/* Chat Tab */}
            <TabsContent value="chat" className="flex-1 flex flex-col min-h-0 mt-0 data-[state=inactive]:hidden">
              <ScrollArea className="flex-1 p-4">
                {messages.length === 0 ? (
                  <div className="text-center text-muted-foreground py-8">
                    <HelpCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p className="text-sm">
                      ご質問をどうぞ。
                      <br />
                      よくある質問から回答をお探しします。
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {messages.map((message, index) => (
                      <div
                        key={index}
                        className={cn(
                          'flex',
                          message.role === 'user' ? 'justify-end' : 'justify-start'
                        )}
                      >
                        <div
                          className={cn(
                            'max-w-[85%] rounded-lg px-4 py-2 text-sm',
                            message.role === 'user'
                              ? 'bg-primary text-primary-foreground'
                              : 'bg-muted'
                          )}
                        >
                          <p className="whitespace-pre-wrap">{message.content}</p>
                          {message.relatedUrl && isSafeUrl(message.relatedUrl) && (
                            <a
                              href={message.relatedUrl}
                              className="block mt-2 text-xs underline hover:no-underline"
                            >
                              → 詳細ページへ
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                    {isLoading && (
                      <div className="flex justify-start">
                        <div className="bg-muted rounded-lg px-4 py-2">
                          <Loader2 className="h-4 w-4 animate-spin" />
                        </div>
                      </div>
                    )}
                    {/* Auto-scroll marker */}
                    <div ref={messagesEndRef} />
                  </div>
                )}
              </ScrollArea>
            </TabsContent>

            {/* History Tab */}
            <TabsContent value="history" className="flex-1 min-h-0 mt-0 data-[state=inactive]:hidden">
              <ScrollArea className="h-full p-4">
                {isLoadingHistory ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin" />
                  </div>
                ) : sessionHistory.length === 0 ? (
                  <div className="text-center text-muted-foreground py-8">
                    <History className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p className="text-sm">履歴がありません</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {sessionHistory.map((session) => (
                      <button
                        key={session.id}
                        onClick={() => handleLoadSession(session)}
                        className="w-full text-left p-3 rounded-lg border hover:bg-muted transition-colors"
                      >
                        <p className="text-sm font-medium truncate">{session.preview}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {formatDateTime(session.created_at)}
                        </p>
                      </button>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </TabsContent>
          </Tabs>

          {/* Input - Only show on chat tab */}
          {activeTab === 'chat' && (
            <form onSubmit={handleSubmit} className="p-4 border-t">
              <div className="flex gap-2">
                <Input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="質問を入力..."
                  disabled={isLoading}
                  className="flex-1"
                />
                <Button
                  type="submit"
                  size="icon"
                  disabled={!input.trim() || isLoading}
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </form>
          )}
        </Card>
      )}
    </>
  );
}

export default FAQChatButton;
